import { useState, useRef, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./Payment.css";
import { config } from "../../../Environment";
import useRetryFetch from "../../../hooks/useRetryFetch";
import { useEffectOnce } from "../../../hooks/useEffectOnce";
import useProcess from "../../../hooks/useProcess";
import { Button } from "../../ui/button";
import {
  Play, Sparkles, ShoppingCart,
} from "lucide-react";
import { toast } from "sonner";
import { decimalAmount, currencyAmount } from "../../../lib/currency";
import { Spinner } from "../../ui/spinner";

function Payment() {
  const navigate = useNavigate();
  const retryFetch = useRetryFetch();

  const location = useLocation();
  const { startProcess, resumeProcess } = useProcess();

  const query = new URLSearchParams(location.search);
  const status = query.get('status');
  const testModeWarning = status === 'testmode';
  const notFoundError = status === 'notfound';
  const paymentId = query.get('paymentId');

  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [payment, setPayment] = useState({});
  const [errorMessage, setErrorMessage] = useState(false);  

  async function fetchPayment() {
    setLoading(true);
    const response = await retryFetch(`${config.API_BASE}/api/payment?id=${encodeURIComponent(paymentId)}`);
    if (response.ok) {
      const result = await response.json();
      if (result.type === 'item') {
        const paymentRecord = result.item;
        const paymentDate = new Date(paymentRecord.updated);
        paymentRecord.displayDate = paymentDate.toDateString(); //.toLocaleDateString();
        setPayment(paymentRecord);

        // for each payment item, fetch the product info
        if (paymentRecord.type === 'product') {
          for (let i = 0; i < paymentRecord.items.length; i += 1) {
            try {
              await fetchPurchasedProduct(paymentRecord, paymentRecord.items[i].fiProduct);
            } catch (err) {
              console.error(`fetcHPayment error while fetching purchased product info ${i}`);
            }
          }
        }
        /*
        window.setTimeout(() => {
          if (typeof window.location.replace === 'function') {
            window.location.replace(result.redirect);
          } else {
            window.location = result.redirect;
          }
        }, 1);
        */

      }
    } else {
      setErrorMessage(true);
    }
    setLoading(false);
  }

  async function fetchPurchasedProduct(paymentRecord, id) {
    setLoading(true);
    const response = await retryFetch(`${config.API_BASE}/api/product/findPurchased?paymentId=${encodeURIComponent(paymentId)}&productId=${encodeURIComponent(id)}`);
    if (response.ok) {
      const result = await response.json();
      if (result.type === 'item') {
        console.log(`product: ${JSON.stringify(result.item)}`);
        if (!paymentRecord.$productMap) {
          paymentRecord.$productMap = {};
        }
        paymentRecord.$productMap[id] = result.item;
        console.log(`fetchPurchasedProduct UPDATED PAYMENT ${JSON.stringify(paymentRecord)}`);
        setPayment(paymentRecord);
      }
    } else {
      //setErrorMessage(true);
      console.log(`fetchPurchasedProduct ${id} error`);
    }
    setLoading(false);
  }

  useEffectOnce(() => {
    fetchPayment();
  }, []);

  function renderTestModeWarning() {
    if (testModeWarning) {
      return (
        <p className="warning">Test mode is required in non-production environments</p>
      );
    }
    return (<></>);
  }

  function renderNotFoundError() {
    if (notFoundError) {
      return (
        <p className="error">Payment not found. Please return to the <a href="/marketplace">{t("marketplace")}</a> and try again.</p>
      );
    }
    return (<></>);
  }

  function renderOtherError() {
    if (errorMessage) {
      return (
        <p className="error">Payment was not completed. Please return to the <a href="/marketplace">{t("marketplace")}</a> and try again.</p>
      );
    }
    return (<></>);
  }

  function renderLoadingImage() {
    if (loading) {
      return (
        <Spinner size="md" />
      );
    } else {
      return (<></>);
    }
  }

  function renderPaymentTitle() {
    switch (payment.status) {
      case 'waiting':
        return (
          <h1 className="text-3xl font-bold">{t("payment_waiting")}</h1>
        );
      case 'review':
        return (
          <h1 className="text-3xl font-bold">{t("payment_review")}</h1>
        );
      case 'declined':
        return (
          <h1 className="text-3xl font-bold">{t("payment_declined")}</h1>
        );
      case 'paid':
        return (
          <h1 className="text-3xl font-bold">{t("payment_receipt")}</h1>
        );
      default:
        return (
          <h1 className="text-3xl font-bold">{t("payment_error")}</h1>
        );
    }
  }


  async function runActivity(id) {
    await startProcess({
      prescriptionId: id,
      onError: () => {
        toast.error("An error occurred while starting the activity.");
      },
    });
  }

  async function returnToEngagement(processId) {
    await resumeProcess({
      processId,
      onError: () => {
        toast.error(
          "An error occurred while continuing the process. Please try again."
        );
      },
    });
  }

  function returnToPortal() {
    navigate("/portal");
  }

  function returnToMarketplace(vendorId) {
    if (vendorId) {
      navigate(`/marketplace/vendor/${vendorId}`);
    } else {
      navigate(`/marketplace`);
    }
  }

  function renderProductAction(productId, custom = {}) {
    if (!payment || !payment.$productMap) {
      return (<></>);
    }
    const product = payment.$productMap[productId];
    if (!product) {
      return (<></>);
    }
    switch (product.type) {
      case 'activity':
        return (
          <div>
              <Button
                size="sm"
                onClick={() => {
                  runActivity(product.data['activity'].fiWorkflow);
                }}
              >
                <Play className="h-4 w-4 mr-1" />
                {t("run")}
              </Button>
          </div>
        );
      case 'service':
        return (
          <div>
              <Button
                size="sm"
                onClick={() => {
                  returnToEngagement(custom.processId);
                }}
              >
                <Play className="h-4 w-4 mr-1" />
                {t("continue")}
              </Button>
          </div>
        );
      case 'mentor_session_credit':
        return (
          <div>
              <Button
                size="sm"
                onClick={() => {
                  returnToEngagement(custom.processId);
                }}
              >
                <Play className="h-4 w-4 mr-1" />
                {t("continue")}
              </Button>
          </div>
        );
      case 'activity_generator_credit':
        return (
          <div>
              <Button
                size="sm"
                onClick={() => {
                  returnToPortal();
                }}
              >
                <Sparkles className="h-4 w-4 mr-1" />
                {t("continue")}
              </Button>
          </div>
        );
      case 'vendor_activity_credit':
        return (
          <div>
              <Button
                size="sm"
                onClick={() => {
                  returnToMarketplace(product.fiVendor);
                }}
              >
                <ShoppingCart className="h-4 w-4 mr-1" />
                {t("continue")}
              </Button>
          </div>
        );
      default:
        return (<></>);
    }
  }

  function renderInvoiceAction(invoiceId) {
    return (<></>); // TODO
  }

  function renderUserSubscriptionAction(userSubscriptionId) {
    return (<></>); // TODO
  }

  function renderItemType(item) {
    const productMap = payment.$productMap ?? {};
    switch (payment.type) {
      case 'product': {
        const product = productMap[item.fiProduct];
        if (!product) {
          return (<span>{t("payment_type_product")}</span>);
        }
        switch (product.type) {
          case 'activity':
            return (<span>{t("product_type_activity")}</span>);
          case 'activity_generator_credit':
            return (<span>{t("product_type_activity_generator_credit")}</span>);
          case 'bundle':
            return (<span>{t("product_type_bundle")}</span>);
          case 'mentor_session_credit':
            return (<span>{t("product_type_mentor_session_credit")}</span>);
          case 'service':
            return (<span>{t("product_type_service")}</span>);
          case 'subscription':
            return (<span>{t("product_type_subscription")}</span>);
          case 'vendor_activity_credit':
            return (<span>{t("product_type_vendor_activity_credit")}</span>);
          default:
            return (<span>{t("payment_type_product")}</span>);
        }
      }
      case 'invoice':
        return (<span>{t("payment_type_invoice")}</span>);
      case 'user_subscription':
        return (<span>{t("payment_type_user_subscription")}</span>);
      default:
        return (<span>{t("payment")}</span>);
    }
  }

  function renderItemAction(item) {
    switch (payment.type) {
      case 'product':
        return renderProductAction(item.fiProduct, item.custom);
      case 'invoice':
        return renderInvoiceAction(item.fiInvoice);
      case 'user_subscription':
        return renderUserSubscriptionAction(item.fiUserSubscription);
      default:
        return (<></>);
    }
  }

  function renderPaymentHeader() {
    if (!payment || !payment.user) {
      return (<></>);
    }
    return (
      <div>
        <p style={{fontSize: "1.2em", color: "#555555", textAlign: "start"}}>Date: {payment.displayDate}</p>
        <p style={{fontSize: "1.2em", color: "#555555", textAlign: "start"}}>Amount: {currencyAmount(payment.amount, payment.currency)}</p>
        <p style={{fontSize: "0.8em", color: "#555555", textAlign: "start"}}>Receipt Number: {payment._id}</p>
        <p style={{fontSize: "0.8em", color: "#555555", textAlign: "start"}}>Customer: {payment.user.displayName}<br/>{payment.user.email}</p>
      </div>
    );
  }

  function renderPaymentItems() {
    if (!Array.isArray(payment?.items)) {
      return (<></>);
    }
    // check if any of the items has a discount applied
    const isDiscount = payment.items.filter((item) => Array.isArray(item.discount && item.discount.length > 0)).length > 0;
    
    const list = [];
    for (let i = 0; i < payment.items.length; i += 1) {
      const item = payment.items[i];
      let discountAmount = 0;
      if (Array.isArray(item.discount)) {
        for (let j = 0; j < item.discount.length; j += 1) {
          discountAmount += item.discount[j];
        }
      }
      list.push(
        <tr key={`${i}_generic`} data-product-id={item.fiProduct}>
          <td>{renderItemType(item)}</td>
          <td>{item.qty}</td>
          <td>{currencyAmount(item.price, payment.currency)}</td>
          {isDiscount ? <td>{currencyAmount(discountAmount)}</td> : ''}
          <td>{currencyAmount(item.subtotal, payment.currency)}</td>
        </tr>
      );
      list.push(
        <tr key={`${i}_detail`}>
          <td colSpan={3} title={item.description}>
            {item.name}
          </td>
          <td colSpan={1}>
            {renderItemAction(item)}
          </td>
        </tr>
      )
    }
    return (
      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th>Qty</th>
            <th>Unit Price</th>
            {isDiscount ? <td>Discount</td> : ''}
            <th>Subtotal</th>
          </tr>
        </thead>
        <tbody>
        {list}
        </tbody>
      </table>
    );
  }

  return (
    <div className="h-full w-full flex items-center justify-start py-12">
      <div className="mx-auto grid w-[450px] gap-6">
        {renderLoadingImage()}
        <div className="grid gap-2 text-center">
          {renderPaymentTitle()}
        </div>
        {renderPaymentHeader()}
        {renderPaymentItems()}
        {renderTestModeWarning()}
        {renderNotFoundError()}
        {renderOtherError()}
      </div>
    </div>
  );
}

export default Payment;
